// A toast with Annulla, since the app's toast has no action. Fixed at the bottom centre, above the publish bar when it
// shows, for 5 seconds. A newer action replaces the one on screen and restarts the clock.
import { useEffect } from "react";
import { cn } from "@/utils/cn";

export interface UndoAction {
  /** Unique per action, so the same message twice still restarts the clock. */
  id: number;
  message: string;
  /** Omitted for a result that has no inverse (the category-wide allergen action): the toast then has no Annulla. */
  undo?: () => void;
}

interface Props {
  action: UndoAction | null;
  onDismiss: () => void;
  /** Px from the bottom of the screen: clears the publish bar when it shows. */
  offset?: number;
  durationMs?: number;
}

export function UndoToast({ action, onDismiss, offset = 16, durationMs = 5000 }: Props) {
  const id = action?.id;
  useEffect(() => {
    if (id === undefined) return undefined;
    const timer = window.setTimeout(onDismiss, durationMs);
    return () => window.clearTimeout(timer);
  }, [id, durationMs, onDismiss]);

  if (!action) return null;
  const { undo } = action;
  return (
    <div
      data-toast-region
      role="status"
      aria-live="polite"
      style={{ bottom: offset }}
      className={cn(
        // pointer-events-auto: an open drawer sets pointer-events none on body, so Annulla after a Duplica in the drawer
        // would not take the tap without it.
        "pointer-events-auto fixed left-1/2 z-[70] -translate-x-1/2 w-max max-w-[min(calc(100vw-32px),28rem)]",
        cn("flex items-center gap-3 rounded-xl bg-gray-900 py-[10px] pl-4 text-[13px] text-white shadow-lg", undo ? "pr-2" : "pr-4"),
        "animate-in fade-in slide-in-from-bottom-2 duration-200",
      )}
    >
      <span className="min-w-0 truncate">{action.message}</span>
      {undo && (
        <button
          type="button"
          onClick={() => {
            undo();
            onDismiss();
          }}
          className="shrink-0 rounded-lg px-2 py-1 font-semibold text-indigo-200 hover:bg-white/10 hover:text-white"
        >
          Annulla
        </button>
      )}
    </div>
  );
}
